'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import TasksInProgress from 'app/[lang]/(main)/translation/(tabs)/tasks-in-progress/_components/TaskInProgressTable';
import { Entering, Pagination } from 'components';
import {
  COMMENTS_DEFAULT,
  LANGUAGES_DEFAULT,
  PAGE_CANT_RESET_DEFAULT,
  PAGE_DEFAULT,
  STATUS_DEFAULT,
  TASK_ID_DEFAULT,
  TRANSLATION_REQUEST_VERSION_DEFAULT
} from 'constants/defaults';
import { FILTERS_TASKS_IN_PROGRESS_PERSISTED_STATE } from 'constants/persistedStates';
import { THEME_TASK_STATUS } from 'constants/status';
import { getComments } from 'factories/getComments';
import { getTranslators } from 'factories/getTranslators';
import {
  CommentsFilterEnum,
  QueryTasksArgs,
  TaskQueryStatusEnum,
  TranslatorAccount
} from 'gql/graphql';
import usePersistentContext from 'hooks/usePersistenceState';
import _ from 'lodash';
import { useTranslations } from 'next-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTasksInProgress } from 'providers/Tasks/useTasksInProgress.ts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { TScheme } from 'types/TScheme';
import { useDebounce } from 'use-debounce';
import { extractParam } from 'utils/paramsUtil';
import { timeAgo } from 'utils/time/timeAgo';
import * as yup from 'yup';
import FiltersTasks from './_components/FiltersTasks';

export interface TasksInProgressForm
  extends Omit<
    QueryTasksArgs,
    'page' | 'perPage' | 'sortOrder' | 'sourceLanguageCode' | 'targetLanguageCode'
  > {
  languages: { sourceLanguageCode: string; targetLanguageCode: string };
  status: TaskQueryStatusEnum[];
}

export default function TasksInProgressPage() {
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [, setQueryParamsFiltersUrl] = usePersistentContext(
    FILTERS_TASKS_IN_PROGRESS_PERSISTED_STATE
  );

  const isInternalUpdate = useRef(false);

  const schema = yup.object().shape({} as TScheme<TasksInProgressForm>);

  const [page, setPage] = useState(1);

  const pageInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Page, PAGE_DEFAULT) as number,
    [searchParams]
  );

  const pageCantResetInitial = useMemo(
    () =>
      extractParam(searchParams, KeysEnum.PageCantReset, PAGE_CANT_RESET_DEFAULT).toString() ===
      'true',
    [searchParams]
  );

  const translationRequestInitial =
    searchParams.get(KeysEnum.TranslationRequestVersion) ?? TRANSLATION_REQUEST_VERSION_DEFAULT;

  const taskIdInitial = searchParams.get(KeysEnum.TaskId) ?? TASK_ID_DEFAULT;

  const languagesInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Languages, LANGUAGES_DEFAULT),
    [searchParams]
  );

  const commentsInitial = useMemo(
    () => (searchParams.get(KeysEnum.Comments) ?? COMMENTS_DEFAULT) as CommentsFilterEnum,
    [searchParams]
  );

  const statusInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Status, STATUS_DEFAULT),
    [searchParams]
  );

  const {
    control,
    setValue,
    formState: { isDirty }
  } = useForm<TasksInProgressForm>({
    resolver: yupResolver(schema)
  });

  const translationRequestVersion = useWatch<any>({
    control,
    name: KeysEnum.TranslationRequestVersion,
    defaultValue: TRANSLATION_REQUEST_VERSION_DEFAULT
  });

  const [deferredTranslationRequestVersion] = useDebounce(translationRequestVersion, 1000);

  const taskId = useWatch<any>({
    control,
    name: KeysEnum.TaskId,
    defaultValue: TASK_ID_DEFAULT
  });

  const [deferredTaskId] = useDebounce(taskId, 1000);

  const languages = useWatch<any>({
    control,
    name: KeysEnum.Languages,
    defaultValue: LANGUAGES_DEFAULT
  });

  const comments = useWatch<any>({
    control,
    name: KeysEnum.Comments,
    defaultValue: COMMENTS_DEFAULT
  });

  const status = useWatch<any>({
    control,
    name: KeysEnum.Status,
    defaultValue: STATUS_DEFAULT
  });

  const filtersAdded = useMemo(
    () =>
      [
        page !== PAGE_DEFAULT,
        deferredTranslationRequestVersion !== TRANSLATION_REQUEST_VERSION_DEFAULT,
        deferredTaskId !== TASK_ID_DEFAULT,
        languages?.sourceLanguageCode !== LANGUAGES_DEFAULT.sourceLanguageCode,
        languages?.targetLanguageCode !== LANGUAGES_DEFAULT.targetLanguageCode,
        comments !== COMMENTS_DEFAULT,
        !!status?.length
      ].some(Boolean),
    [
      comments,
      languages?.sourceLanguageCode,
      languages?.targetLanguageCode,
      page,
      status?.length,
      deferredTaskId,
      deferredTranslationRequestVersion
    ]
  );

  const resetFilters = useCallback(() => {
    setPage(pageInitial);
    setValue(KeysEnum.TranslationRequestVersion, translationRequestInitial);
    setValue(KeysEnum.TaskId, taskIdInitial);
    setValue(KeysEnum.Languages, languagesInitial);
    setValue(KeysEnum.Comments, commentsInitial);
    setValue(KeysEnum.Status, statusInitial);
  }, [
    commentsInitial,
    languagesInitial,
    pageInitial,
    setValue,
    statusInitial,
    taskIdInitial,
    translationRequestInitial
  ]);

  const {
    data: tasksInProgress,
    isLoading,
    isFetching
  } = useTasksInProgress({
    page,
    perPage: 20,
    sourceLanguageCode: languages?.sourceLanguageCode,
    targetLanguageCode: languages?.targetLanguageCode,
    comments,
    status,
    translationRequestVersion: deferredTranslationRequestVersion,
    taskId: deferredTaskId
  });

  const totalPages = tasksInProgress?.pagesCount ?? 0;

  const rows =
    tasksInProgress?.nodes?.map((task) => {
      const themeStatus = THEME_TASK_STATUS[task.status];

      const { comments, commentsNew } = getComments(task?.comments);

      const relatedRequests = task?.precedentTrs?.map((request) => request?.version);

      const translators = getTranslators(
        (task?.translatorAccount as TranslatorAccount)?.user?.name,
        task?.proofreaderAccount?.user?.name
      );

      return {
        id: task?.id,
        readyToReview: timeAgo(task?.waitingInReviewSince, t),
        language:
          task?.sourceLanguageCode?.toLocaleUpperCase() +
          ' - ' +
          task?.targetLanguageCode?.toLocaleUpperCase(),
        quality: t(`ordering_form.qualities.${task.orderQuality}` as any),
        totalWords: task.totalTranslatableWordCount ?? 0,
        icon: themeStatus?.icon,
        color: themeStatus?.color,
        comments,
        commentsNew,
        translators,
        relatedRequests,
        relatedRequestsQnt:
          t('components.tasks_languages.translation_request_ids') + ` (${relatedRequests?.length})`
      };
    }) ?? [];

  const isRetrieving = isLoading && page === 1;

  const handleClear = useCallback(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      url.searchParams.delete(KeysEnum.Page);
      url.searchParams.delete(KeysEnum.TranslationRequestVersion);
      url.searchParams.delete(KeysEnum.TaskId);
      url.searchParams.delete(KeysEnum.Languages);
      url.searchParams.delete(KeysEnum.Comments);
      url.searchParams.delete(KeysEnum.Status);

      resetFilters();

      window.history.replaceState({}, '', url.toString());
    }
  }, [resetFilters]);

  useEffect(() => {
    isInternalUpdate.current = true;
    const debounceMs = pageCantResetInitial ? 2000 : 0;

    const timer = setTimeout(() => {
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);

        if (deferredTranslationRequestVersion)
          url.searchParams.set(
            KeysEnum.TranslationRequestVersion,
            deferredTranslationRequestVersion
          );
        else url.searchParams.delete(KeysEnum.TranslationRequestVersion);

        if (deferredTaskId) url.searchParams.set(KeysEnum.TaskId, deferredTaskId);
        else url.searchParams.delete(KeysEnum.TaskId);

        if (
          languages?.sourceLanguageCode !== undefined &&
          languages?.targetLanguageCode !== undefined
        )
          url.searchParams.set(KeysEnum.Languages, JSON.stringify(languages));
        else url.searchParams.delete(KeysEnum.Languages);

        if (comments) url.searchParams.set(KeysEnum.Comments, comments);
        else url.searchParams.delete(KeysEnum.Comments);

        if (status?.length) url.searchParams.set(KeysEnum.Status, JSON.stringify(status));
        else url.searchParams.delete(KeysEnum.Status);

        url.searchParams.delete(KeysEnum.PageCantReset);
        url.searchParams.delete(KeysEnum.Page);

        setPage(pageInitial);
        if (pageCantResetInitial) setPage(pageInitial);
        else
          setPage(
            [
              translationRequestInitial !== deferredTranslationRequestVersion,
              taskIdInitial !== deferredTaskId,
              !_.isEqual(languagesInitial, languages),
              commentsInitial !== comments,
              statusInitial !== status,
              taskIdInitial !== deferredTaskId
            ].some(Boolean) && isDirty
              ? PAGE_DEFAULT
              : pageInitial
          );

        setQueryParamsFiltersUrl(url.toString());
        window.history.replaceState({}, '', url.toString());
      }

      setTimeout(() => {
        isInternalUpdate.current = false;
      }, 100);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [
    comments,
    languages,
    pageInitial,
    pathname,
    status,
    deferredTaskId,
    deferredTranslationRequestVersion,
    translationRequestInitial,
    taskIdInitial,
    languagesInitial,
    commentsInitial,
    statusInitial,
    pageCantResetInitial,
    setQueryParamsFiltersUrl
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (page > PAGE_DEFAULT) url.searchParams.set(KeysEnum.Page, JSON.stringify(page));
      else url.searchParams.delete(KeysEnum.Page);

      setQueryParamsFiltersUrl(url.toString());
      window.history.replaceState({}, '', url.toString());
    }
  }, [page, pathname, setQueryParamsFiltersUrl]);

  useEffect(() => {
    resetFilters();
  }, [resetFilters]);

  return (
    <>
      <div className="mt-5 flex flex-row md:gap-4 lg:gap-8">
        <div className="w-1/4">
          <Entering offset={20} delay={0.6} duration={0.2} position="left">
            <FiltersTasks control={control} filtersAdded={filtersAdded} onClear={handleClear} />
          </Entering>
        </div>
        <Entering offset={5} delay={0.8} duration={0.2} position="bottom" className="w-3/4 gap-10">
          <TasksInProgress rows={rows} isLoading={isRetrieving} isFetching={isFetching} />
          {!!totalPages && !!page && !isRetrieving && (
            <div className="flex justify-center py-10">
              <Pagination totalPages={totalPages} page={page} changePage={setPage} />
            </div>
          )}
        </Entering>
      </div>
    </>
  );
}
